'use client'

import { ExternalLinkIcon, LinkIcon, OctagonX } from 'lucide-react'

import { SplitsLink as Link } from '~/components/Link'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '~/components/component-library/Dropdown'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { RejectTransactionDialog } from '~/components/smart-accounts/RejectTransactionDialog'
import { UpdateTransactionMemoDropdownItem } from '~/components/smart-accounts/UpdateTransactionMemoDropdownItem'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgId } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useDeleteTransactionBatch } from '~/hooks/useTransactionBatch'
import { useTransactionProperties } from '~/hooks/useTransactions'
import { OrgTransaction } from '~/types'
import { getExplorerUserOpLink } from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { isDraftBatchTransaction } from '~/utils/smartAccounts/batch'
import { getTransactionUrl } from '~/utils/smartAccounts/transactions'

export const TransactionDropdownMenuItems = ({
  transaction,
}: {
  transaction: OrgTransaction
}) => {
  const regularTransaction =
    !transaction.isPublicTransaction && transaction.isMerkleTransaction
      ? undefined
      : transaction

  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const hasAccess = !!authUser && transaction.orgId === activeOrgId

  const { isSubmitted, canReject } = useTransactionProperties(transaction)
  const { deleteBatch } = useDeleteTransactionBatch()

  const isDraftBatch = isDraftBatchTransaction(transaction)

  const handleCancel = isDraftBatch
    ? () => {
        setGlobalModalState({
          isOpen: true,
          content: (
            <SplitsDialogContent
              id="cancelDraftBatch"
              variant="destructive"
              title={
                <>
                  Cancel{' '}
                  <b>
                    <TransactionTitle
                      transaction={transaction}
                      isSmall
                      unstyled
                    />
                  </b>
                  ?
                </>
              }
              deleteButton={{
                text: 'Yes, cancel',
                onClick: () => {
                  void deleteBatch({
                    smartAccountId: transaction.smartAccountId,
                    transactionId: transaction.id,
                  })
                  closeGlobalModal()
                },
                eventProperties: {
                  transactionId: transaction.id,
                },
              }}
              cancelButton={{
                text: 'Keep it',
                eventProperties: {
                  transactionId: transaction.id,
                },
              }}
            >
              <p>This will discard all transactions in the batch</p>
            </SplitsDialogContent>
          ),
        })
      }
    : () => {
        setGlobalModalState({
          isOpen: true,
          content: (
            <RejectTransactionDialog
              setIsOpen={() => {
                setGlobalModalState({ isOpen: false, content: null })
              }}
              orgId={transaction.orgId}
              transaction={transaction}
            />
          ),
        })
      }

  const cancelItem = (
    <DropdownMenuItem onClick={handleCancel}>
      <OctagonX className="text-error" />
      <div>Cancel</div>
    </DropdownMenuItem>
  )

  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          const url = getTransactionUrl(transaction)
          copyToClipboard(url, 'Link copied to clipboard')
        }}
      >
        <LinkIcon />
        <div>Copy link</div>
      </DropdownMenuItem>
      {regularTransaction && isSubmitted && (
        <Link
          path={getExplorerUserOpLink(
            regularTransaction.userOpHash,
            regularTransaction.chainId,
          )}
          target="tab"
        >
          <DropdownMenuItem>
            <ExternalLinkIcon />
            <div>Open in explorer</div>
          </DropdownMenuItem>
        </Link>
      )}
      {canReject && transaction.orgId && (
        <>
          <DropdownMenuSeparator />
          {cancelItem}
        </>
      )}
      {hasAccess && (
        <>
          <DropdownMenuSeparator />
          <UpdateTransactionMemoDropdownItem transaction={transaction} />
        </>
      )}
    </>
  )
}
